<script setup lang="ts">
import { Hour } from "@mmh/types"
import { Mail, MapPin, Phone } from "lucide-vue-next"
import type { HTMLAttributes } from "vue"
import { computed, markRaw, onMounted, ref, watch } from "vue"
import { ThemeButton } from "~/components/theme"
import { Separator } from "~/components/ui/separator"
import { useConcept } from "~/composables/concept"
import { useLocation } from "~/composables/locations"
import { useTime } from "~/composables/time"
import { sentenceCase } from "~/lib/case"
import { cn } from "~/lib/utils"

const { class: cl } = defineProps<{
    class?: HTMLAttributes["class"]
}>()

const { concept } = useConcept()
const { location, hasOneLocation, contactPath } = useLocation()
const { formatTime } = useTime("America/Los_Angeles")

const capsize = computed(() => concept.value.capsize)

const addressString = computed(() => {
    const address = location.value?.address
    if (!address) return ""
    return `${address.street}, ${address.city}, ${address.state} ${address.zip}`
})

const phoneNumber = computed(() => location.value?.contact.phone || "")
const email = computed(() => location.value?.contact.email || "")

const mapsUrl = computed(() => {
    const conceptName = `${concept.value.name} ${hasOneLocation ? "" : location.value?.name}`.trim()
    return `https://maps.google.com?q=${encodeURIComponent(conceptName + " " + addressString.value)}`
})

const telUrl = computed(() => {
    return `tel:${phoneNumber.value.replace(/[^0-9]/g, "")}`
})

const mailtoUrl = computed(() => {
    return `mailto:${email.value}`
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const map = ref<any>()
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const marker = ref<any>()

onMounted(async () => {
    const lat = location.value?.lat
    const lon = location.value?.lon
    if (lat == null || lon == null) return
    
    // Dynamic import to avoid SSG crash - maplibre-gl needs browser APIs
    const maplibre = await import("maplibre-gl")
    await import("maplibre-gl/dist/maplibre-gl.css")
    // W5Iv8FII6TdvQCqGaxGH => @gmail
    // MTM2fVYTzOMgn0aEL5IM => @seacoast
    
    const maptilerKey = import.meta.env.DEV ? "" : "MTM2fVYTzOMgn0aEL5IM"
    map.value = markRaw(new maplibre.Map({
        container: "map",
        style: `https://api.maptiler.com/maps/streets-v2-dark/style.json?key=${maptilerKey}`,
        center: [lon, lat],
        zoom: 13,
        scrollZoom: false,
        interactive: false,
        attributionControl: false
    }))
    map.value.addControl(new maplibre.AttributionControl({ compact: false }))
    marker.value = markRaw(new maplibre.Marker()
        .setLngLat([lon, lat])
        .addTo(map.value)
    )

    // Keep visual attribution, but exclude controls from tab order under aria-hidden
    const disableMapTabOrder = () => {
        const container = map.value.getContainer() as HTMLElement
        const attrib = container.querySelector(".maplibregl-ctrl-attrib")
        if (attrib instanceof HTMLDetailsElement) {
            attrib.open = true
        }
        for (const el of container.querySelectorAll<HTMLElement>("a, button, summary, [tabindex]")) {
            el.tabIndex = -1
        }
    }
    disableMapTabOrder()
    map.value.on("load", disableMapTabOrder)
})

watch(location, async (location, oldLocation) => {
    if (location && location.path !== oldLocation?.path) {
        const lat = location.lat
        const lon = location.lon
        map.value?.setCenter([lon, lat])
        marker.value?.setLngLat([lon, lat])
    }
}, { immediate: true })

type Hours = Hour[]
type DaysOfWeek = "sunday" | "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday";

const dayAbbreviations: Record<string, string> = {
    sunday: "Sun",
    monday: "Mon",
    tuesday: "Tues",
    wednesday: "Wed",
    thursday: "Thurs",
    friday: "Fri",
    saturday: "Sat"
}

function formatHours(hours: Hours): Record<string, string> {
    const numberToDay: Record<number, DaysOfWeek> = {
        0: "sunday", 1: "monday", 2: "tuesday", 3: "wednesday",
        4: "thursday", 5: "friday", 6: "saturday"
    }

    // Group days by time
    const timeRanges: Record<string, number[]> = {}
    for (const { days, times } of hours) {
        const timeKey = times.join("-")
        timeRanges[timeKey] = [...(timeRanges[timeKey] || []), ...days]
    }

    // Build entries with start day for sorting
    const entries: Array<{ startDay: number, label: string, time: string }> = []
    
    for (const [timeKey, days] of Object.entries(timeRanges)) {
        const [open, close] = timeKey.split("-")
        const formattedTime = `${formatTime(open)} - ${formatTime(close)}`
        const openTime = formattedTime.split("-")[0]
        const openTimeWithoutSeconds = openTime.replace(":00", "")
        const formattedTimeWithoutOpenSeconds = formattedTime.replace(openTime, openTimeWithoutSeconds)
        const uniqueDays = [...new Set(days)].sort((a, b) => a - b)
        
        // Check for Saturday-Sunday wraparound
        const hasSatSunWrap = uniqueDays.includes(6) && uniqueDays.includes(0) && 
                              uniqueDays[uniqueDays.length - 1] === 6 && uniqueDays[0] === 0
        
        if (hasSatSunWrap && uniqueDays.length === 2) {
            // Special case: just Saturday and Sunday with same hours
            entries.push({
                startDay: 6,
                label: "Sat - Sun",
                time: formattedTimeWithoutOpenSeconds
            })
            continue
        }
        
        // Group consecutive days
        let currentRange: number[] = []
        const ranges: number[][] = []
        
        for (const day of uniqueDays) {
            if (currentRange.length === 0 || day === currentRange[currentRange.length - 1] + 1) {
                currentRange.push(day)
            } else {
                ranges.push([...currentRange])
                currentRange = [day]
            }
        }
        if (currentRange.length > 0) {
            ranges.push(currentRange)
        }

        for (const range of ranges) {
            const startDay = range[0]
            const endDay = range[range.length - 1]
            const startDayName = dayAbbreviations[numberToDay[startDay]]
            const endDayName = dayAbbreviations[numberToDay[endDay]]
            
            entries.push({
                startDay,
                label: range.length === 1 ? startDayName : `${startDayName} - ${endDayName}`,
                time: formattedTimeWithoutOpenSeconds
            })
        }
    }
    
    // Determine if we should start with Sunday or Monday
    // If any entry starts with Sunday (0), use Sunday-first sorting
    // Otherwise, use Monday-first sorting
    const hasSundayStart = entries.some(entry => entry.startDay === 0)
    
    entries.sort((a, b) => {
        if (hasSundayStart) {
            // Sunday-first: 0, 1, 2, 3, 4, 5, 6
            return a.startDay - b.startDay
        } else {
            // Monday-first: 1, 2, 3, 4, 5, 6, 0
            const aDay = a.startDay === 0 ? 7 : a.startDay
            const bDay = b.startDay === 0 ? 7 : b.startDay
            return aDay - bDay
        }
    })
    
    // Build result object
    const result: Record<string, string> = {}
    for (const entry of entries) {
        result[entry.label] = entry.time
    }
    
    return result
}

const formattedHours = computed(() => location.value?.hours ? formatHours(location.value.hours) : {})
const specialHours = computed(() => {
    const value = location.value?.pageContent?.hoursLocationContact?.specialHours
    return value?.replace(/<[^>]*>/g, "").trim() ? value : undefined
})
const hasCoordinates = computed(() => location.value?.lat != null && location.value?.lon != null)
</script>

<template>
    <section
        id="hours-location"
        tabindex="-1"
        :class="cn('space-y-10 md:space-y-12 px-4 sm:px-6', cl)"    
    >
        <h2 class="text-2xl sm:text-3xl lg:text-4xl text-center font-headline text-balance max-w-full overflow-hidden">
            Hours & Location
        </h2>

        <div class="flex flex-col items-center gap-16">
            <div class="w-full max-w-3xl space-y-10">
                <div class="flex flex-col md:flex-row gap-16">
                    <div class="space-y-6 flex-1">
                        <header class="space-y-4">
                            <h3 class="uppercase text-foreground-muted">
                                Operating Hours
                            </h3>
                            <Separator class="w-10 bg-foreground/20" />
                        </header>
                        <div class="space-y-8">
                            <div
                                v-for="(hours, day) in formattedHours"
                                :key="day"
                                class="flex items-start justify-between gap-4 min-w-0"
                            >
                                <span class="text-balance text-foreground-muted-darker text-xs md:text-sm font-button font-medium capsize shrink-0">{{ day }}</span>
                                <Separator :class="cn('flex-1 bg-foreground/10 mt-1', capsize ? 'mt-1' : 'mt-2.5')" />
                                <span class="shrink-0 text-balance text-xs md:text-sm font-button font-medium capsize lowercase break-words max-w-full">{{ hours }}</span>
                            </div>
                        </div> 
                    </div>

                    <div
                        v-if="location?.services?.length"
                        class="space-y-6 flex-1"
                    >
                        <header class="space-y-4">
                            <h3 class="uppercase text-foreground-muted">
                                Service Hours
                            </h3>
                            <Separator class="w-10 bg-foreground/20" />
                        </header>
                        <div class="space-y-4">
                            <div 
                                v-for="(service, idx) in location?.services"
                                :key="idx"
                                class="flex items-start gap-2 min-w-0"
                            >
                                <span class="text-foreground-muted-darker text-xs md:text-sm font-button font-medium capsize shrink-0 text-balance max-w-20 md:max-w-24">{{ sentenceCase(service.name) }}</span>
                                <Separator :class="cn('flex-1 bg-foreground/10 mt-1', capsize ? 'mt-1.5' : 'mt-2.5')" />
                                <div :class="cn('text-right flex flex-col items-end min-w-0', capsize ? 'gap-3' : 'gap-0.5')">
                                    <span
                                        class="text-xs md:text-sm font-button font-medium capsize text-balance"
                                        style="overflow-wrap: anywhere;"
                                        v-html="service.serviceHoursCopy"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="specialHours"
                    class="flex justify-center text-center pt-6 mt-6 border-t border-foreground/10"
                >
                    <div 
                        class="text-balance text-foreground text-sm md:text-base font-button font-bold tracking-wider bg-primary/20 px-4 py-2 rounded-md"
                        v-html="specialHours"
                    />
                </div>
            </div>

            <div
                v-if="hasCoordinates"
                id="map"
                aria-hidden="true"
                class="relative w-full max-w-3xl h-72"
            />

            <div class="w-full max-w-3xl flex flex-col md:flex-row gap-16"> 
                <div class="space-y-8 w-full md:w-5/8">
                    <header class="space-y-2">
                        <h3 class="uppercase text-foreground-muted">
                            Parking
                        </h3>
                        <Separator class="w-10 bg-foreground/20" />
                    </header>
                    <p
                        class="text-balance text-xs md:text-sm font-button font-medium capsize text-foreground"
                        v-html="location?.pageContent?.hoursLocationContact?.parking"
                    />
                </div>

                <div class="space-y-8 w-full md:w-3/8 flex flex-col items-center">
                    <header class="space-y-2">
                        <h3 class="uppercase text-foreground-muted whitespace-nowrap">
                            Location & Contact
                        </h3>
                        <Separator class="w-10 bg-foreground/20" />
                    </header>
                    <div class="space-y-4 md:space-y-0">
                        <a 
                            :href="mapsUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            :aria-label="`Address: ${addressString}`"
                            tabindex="0"
                            class="flex-1 w-full md:w-fit flex items-center gap-4 font-button font-medium text-sm min-h-16 p-6 rounded border border-foreground/20 md:border-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                        >
                            <MapPin
                                class="size-4 text-foreground-muted"
                                aria-hidden="true"
                            />
                            <div class="flex flex-col items-start gap-2">
                                <span class="capsize">{{ location?.address?.street }}</span>
                                <span class="capsize">{{ location?.address?.city }}, {{ location?.address?.state }} {{ location?.address?.zip }}</span>
                            </div>
                        </a>
                        <a 
                            :href="telUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            :aria-label="`Phone: ${phoneNumber}`"
                            tabindex="0"
                            class="flex-1 w-full md:w-fit flex items-center gap-4 font-button font-medium text-sm min-h-16 p-6 rounded border border-foreground/20 md:border-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                        >
                            <Phone
                                class="size-4 text-foreground-muted"
                                aria-hidden="true"
                            />
                            <span class="capsize">{{ phoneNumber }}</span>
                        </a>
                        <a 
                            :href="mailtoUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            :aria-label="`Email: ${email}`"
                            tabindex="0"
                            class="flex-1 w-full md:w-fit flex items-center gap-4 font-button font-medium text-sm min-h-16 p-6 rounded border border-foreground/20 md:border-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                        >
                            <Mail
                                class="size-4 text-foreground-muted"
                                aria-hidden="true"
                            />
                            <span class="capsize">{{ email }}</span>
                        </a>
                    </div>
                    <ThemeButton
                        size="lg"
                        as-child
                        tabindex="0"
                    >
                        <RouterLink :to="contactPath()">
                            Contact Us
                        </RouterLink>
                    </ThemeButton>
                </div>
            </div>
        </div> 
    </section>
</template>

<style>
/* MapLibre injects attrib DOM at runtime; unscoped + !important needed to override its CSS */
#hours-location #map .maplibregl-ctrl-attrib {
    background-color: #fff !important;
    color: #111 !important;
}
#hours-location #map .maplibregl-ctrl-attrib a,
#hours-location #map .maplibregl-ctrl-attrib-inner {
    color: #111 !important;
}
</style>